import { useState, useEffect, useRef } from "react";

// MUI Core Components
import {
  Card,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Tooltip,
  Button,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";

// MUI Icons
import LockIcon from "@mui/icons-material/Lock";
import HelpIcon from "@mui/icons-material/Help";
import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import YouTubeIcon from "@mui/icons-material/YouTube";
import InfoIcon from "@mui/icons-material/Info";
import QuizIcon from "@mui/icons-material/Quiz";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import * as MuiIcons from "@mui/icons-material";


// Custom Components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// App Modules
import AssessmentComponent from "../Assessment/AssessmentComponent";
import useQuizState from "../Assessment/useQuizState";
import CrudService from "services/cruds-service";
import CourseGoalModal from "../CourseHome/Dialog";

// External Libraries
import MDEditor from "@uiw/react-md-editor";
import ReactPlayer from "react-player";

import { useMaterialUIController, setMiniSidenav } from "context";

import { analytics } from "layouts/dashboards/analytics/analytics";
import { events } from "layouts/dashboards/analytics/events";

function LinkRenderer(props) {
  return (
    <a href={props.href} target="_blank" rel="noreferrer">
      {props.children}
    </a>
  );
}

function CourseModule({
  courseName,
  courseId,
  courseCompletionStatus,
  coursePurchaseStatus,
  user,
  modules,
  isPreviewMode = false,
  paymentLink = "",
}) {
  const [controller, dispatch] = useMaterialUIController();

  useEffect(() => {
    // Collapse sidenav on load
    setMiniSidenav(dispatch, true);
  }, [dispatch]);

  const [courseLevel, setCourseLevel] = useState(0);
  const [selectedModule, setSelectedModule] = useState(0);
  const [openModule, setOpenModule] = useState(0);
  const [completedModules, setCompletedModules] = useState([]);
  const itemRefs = useRef([])
  useEffect(() => {
    if (openModule !== null) {
      const el = itemRefs.current[openModule]
      el?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      })
    }

  }, [openModule])
  const [selectedContentIndex, setSelectedContentIndex] = useState(0); // Keeps track of the content index within the selected module
  const [loading, setLoading] = useState({ isloading: false, message: "" });
  const isPaidPreview = !!paymentLink && !coursePurchaseStatus;
  const [goalModalOpen, setGoalModalOpen] = useState(false);
  const [registrationPromptOpen, setRegistrationPromptOpen] = useState(false);

  const handleRegisterFromPrompt = () => {
    if (!paymentLink) return;
    window.open(paymentLink, "_blank");
    setRegistrationPromptOpen(false);
  };

  const handleLockedClick = () => {
    if (isPaidPreview) {
      setRegistrationPromptOpen(true);
    } else {
      setGoalModalOpen(true);
    }
  };

  const handlePurchaseCourse = async (goal) => {
    try {
      const response = await CrudService.purchaseCourse({
        user_id: user,
        course_id: courseId,
        goal: goal,
      });
      if (response) {
        analytics.track(events.COURSE_ENROLLED, { userId: user, courseId: courseId, goal: goal });
        alert("Course purchased successfully. The course is now unlocked for you in My Courses.");
        window.location.reload();
      } else {
        alert("Error purchasing course. Please try again later.");
      }
    } catch (error) {
      console.error(error);
      alert("Error purchasing the course. Please try again later.");
    }
  };

  const [detailResponse, setDetailResponse] = useState({
    user_name: "",
    course_name: "",
    course_description: "",
    base64_data: "",
    course_modules_list: [],
  });
  const {
    quizLoader,
    setQuizLoader,
    currentQuestion,
    setCurrentQuestion,
    quizCertificate,
    setQuizCertificate,
    quizResults,
    setQuizResults,
    quizSubmitted,
    setQuizSubmitted,
    answeredQuestions,
    setAnsweredQuestions,
    performanceByModule,
    setPerformanceByModule,
    performance_data,
    setPerformanceData,
  } = useQuizState();

  const contentTitles = {
    info: "Overview",
    reading: "Slide Deck",
    video: "Video Lecture",
    quiz: "Assessment",
  };


  useEffect(() => {
    if (isPreviewMode || !user) return;
    const fetchCompletedModules = async () => {
      try {
        const response = await CrudService.getCompletedModules(user, courseId);
        if (response?.completed_modules) {
          setCompletedModules(response.completed_modules);
        }
      } catch (error) {
        console.error("Error fetching completed modules:", error);
      }
    };

    fetchCompletedModules();
  }, [user, courseId]);


  const [showModulesList, setShowModulesList] = useState(true);

  // Ensure courseModules has data before trying to access its properties
  const selectedModuleData = modules[selectedModule] || {};
  const selectedType = selectedModuleData.resources[selectedContentIndex].type;
  const isModuleCompleted = completedModules.includes(selectedModuleData._id);
  const isLastModule = selectedModule === modules.length - 1;
  const isLastResourceInModule =
    selectedContentIndex === modules[selectedModule].resources.length - 1;
  const isModuleEnd = isLastResourceInModule;


  const handleNext = () => {
    if (!isModuleEnd)
      setSelectedContentIndex(selectedContentIndex + 1)
    else if (!isLastModule) {
      setSelectedContentIndex(0)
      setSelectedModule(selectedModule + 1)

    }
  };

  const handlePrevious = () => {
    if (selectedContentIndex !== 0)
      setSelectedContentIndex(selectedContentIndex - 1)
    else if (selectedModule !== 0) {
      setSelectedContentIndex(0)
      setSelectedModule(selectedModule - 1)
    }
  };

  const submitQuizResults = async () => {
    // Set the UI to indicate loading and submission process
    setQuizLoader(true);
    setQuizSubmitted(true);

    // Log the submission attempt

    try {
      // Await the response from submitting the quiz

      const response = await CrudService.submitQuiz({
        course_id: courseId,
        user_id: user,
        answered_questions: answeredQuestions,
      });

      // Process the response after it's received
      setQuizResults(response);

      // Calculate performance by modules from the response
      const performanceByModules = {};

      for (let i = 0; i < response.questions.length; i++) {
        const module = response.questions[i].module;

        const score =
          response.questions[i].user_answer ==
            response.questions[i].answer_option
            ? 1
            : 0;

        if (performanceByModules[module]) {
          performanceByModules[module].score += score;
          performanceByModules[module].total += 1;
        } else {
          performanceByModules[module] = { score, total: 1 };
        }
      }
      // convert the dictionary to an array with the key as module names
      const performanceByModule = Object.keys(performanceByModules).map(
        (module) => {
          const { score, total } = performanceByModules[module];
          return { module, score, total };
        }
      );
      setPerformanceByModule(performanceByModule);

      // Prepare performance data for display
      const performanceData = {
        columns: [
          { Header: "Module", accessor: "module" },
          { Header: "Score", accessor: "score" },
          { Header: "Total", accessor: "total" },
        ],
        rows: performanceByModule,
      };
      setPerformanceData(performanceData);

      // Update the state with the new questions from the response
      setQuizQuestions(response.questions);
    } catch (error) {
      console.error("Failed to submit quiz results", error);
    } finally {
      // Hide loader irrespective of success or failure
      setQuizLoader(false);
    }
  };

  // Show loading spinner while fetching courses
  if (loading.isloading) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress color="info" />
        <MDTypography variant="h4" style={{ marginLeft: 16 }}>
          {loading.message}
        </MDTypography>
      </MDBox>
    );

  }

  const handleMarkAsComplete = async () => {
    try {
      await CrudService.completeModule(user, courseId, selectedModuleData._id);
      if (!completedModules.includes(selectedModuleData._id)) {
        setCompletedModules((prev) => [...prev, selectedModuleData._id]);
      }
    } catch (error) {
      console.error("Failed to mark module as complete", error);
    }
  };
  return (
    <Card id="slides" sx={{ overflow: "visible" }}>
      <MDBox pr={3}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center">
          {/* Left-aligned Course Name */}
          <MDTypography variant="h2" mt={3} mx={4}>
            {courseName}
          </MDTypography>

          {/* <MDBox
            display="flex"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            mt={2}
          >
            <MDTypography variant="h4">
              Course Level:
            </MDTypography>

            <FormControl
              sx={{ minWidth: 180, flex: "1 1 auto" }}  // Increased minWidth for better appearance
              size="medium"  // Using "medium" for a larger size
            >
              <InputLabel id="course-level-label" sx={{ fontSize: "1.2rem" }}>Level</InputLabel> 
              <Select
                labelId="course-level-label"
                id="course-level-select"
                value={courseLevel}
                label="Level"
                sx={{
                  fontSize: "1.2rem", // Larger font size for the Select input
                  padding: "10px",    // Increase padding for larger click area
                  height: "48px",     // Increase the height for a more prominent size
                }}
              >
                <MenuItem value={0} sx={{ fontSize: "1.2rem" }}>Explore</MenuItem>  
                <MenuItem value={10} sx={{ fontSize: "1.2rem" }}>Experience</MenuItem>
                <MenuItem value={20} sx={{ fontSize: "1.2rem" }}>Excel</MenuItem>
              </Select>
            </FormControl>

            <Tooltip
              title="Select the course level to match the learner’s expertise — Explore for beginners, Experience for intermediate learners, and Excel for advanced users. This will tailor the content’s depth, complexity, and tone accordingly."
              arrow
            >
              <HelpIcon sx={{ fontSize: 24, cursor: "pointer" }} />
            </Tooltip>
          </MDBox> */}


        </MDBox>
        <Button
          color="info"
          style={{
            display: showModulesList ? "none" : "block",
            paddingTop: "1rem",
            marginBottom: "-0.5rem"
          }}
          onClick={() => setShowModulesList(true)}
        >
          <span style={{ verticalAlign: "middle" }}>Show Modules &nbsp;{" "}</span>
          <Icon
            style={{
              verticalAlign: "middle",
            }}
          >
            chevron_right
          </Icon>
        </Button>
        <MDBox py={showModulesList ? 1 : 0} px={3} lineHeight={1}>
          <Grid container>
            {showModulesList && (
              <Grid
                item
                xs={12}
                md={3}
                style={{
                  padding: "1rem",
                }}
              >
                <Button
                  color="error"
                  onClick={() => setShowModulesList(false)}
                  style={{
                    display: "inline-flex",
                    alignItems: "right",
                    justifyContent: "flex-end",
                    width: "100%",
                  }}
                >
                  <span style={{ verticalAlign: "middle", paddingTop: "2px" }}>
                    Hide Modules &nbsp;
                  </span>
                  <Icon style={{ verticalAlign: "middle", paddingTop: "-5px" }}>menu_open</Icon> {/* Adjusts vertical alignment */}
                </Button>


                <List style={{ borderRadius: "5px", height: "50vh", overflowY: "scroll", scrollbarWidth: "none" }}>
                  {
                    modules.map((module, index) => {
                      const isUnlocked = index === 0 || (coursePurchaseStatus && completedModules.includes(modules[index - 1]?._id));
                      const isSelected = index === selectedModule;
                      return (
                        <div key={index} ref={el => (itemRefs.current[index] = el)}>

                          <ListItem
                            onClick={() => {
                              if (isPreviewMode && index > 0) {
                                handleLockedClick();
                                return;
                              }
                              if (index > 0 && !coursePurchaseStatus) {
                                analytics.track(events.LOCKED_COURSE_ATTEMPT, {
                                  course_id: courseId,
                                  module_id: index,
                                  user_id: user,
                                });
                                handleLockedClick();
                                return;
                              }
                              if (!isUnlocked) return;
                              setSelectedModule(index);
                              setSelectedContentIndex(0); // Reset content index for the new module
                              setOpenModule(openModule === index ? null : index); // Toggle the dropdown open/close for the clicked module
                            }}
                            disabled={!isPreviewMode && index > 0 && !coursePurchaseStatus}
                            style={{
                              opacity: !isUnlocked ? 0.5 : 1,
                              backgroundColor: index === selectedModule ? "#f0f0f0" : "white",
                              paddingLeft: "1rem",
                              paddingRight: "1rem",
                              paddingTop: "1rem",
                              paddingBottom: "10px",
                              borderBottom: index === selectedModule ? "none" : "1px solid #e0e0e0",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                width: "100%",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              {/* <ListItemText primary={`Module ${index + 1}`} secondary={module.description} /> */}
                              <ListItemText
                                primary={
                                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                    {module.name}
                                  </div>
                                }
                              />
                              <MDBox display="flex" alignItems="center" gap="0.5rem">
                                {(!isUnlocked || (!coursePurchaseStatus && index > 0)) && (
                                  <Tooltip title={(!coursePurchaseStatus && index > 0) ? "Register to unlock this module" : "Complete the previous module to unlock"}>
                                    <Icon color="disabled">lock</Icon>
                                  </Tooltip>
                                )}
                                {
                                  completedModules.includes(module._id) && (
                                    <CheckCircleIcon sx={{ color: "green", fontSize: "1.2rem" }} />
                                  )}
                                <IconButton
                                  size="small"
                                  disabled={!isUnlocked}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                  }}
                                >
                                  {openModule === index ? <ExpandLess /> : <ExpandMore />}
                                </IconButton>
                              </MDBox>
                            </div>
                          </ListItem>

                          <Collapse in={selectedModule === index && isUnlocked} timeout="auto" unmountOnExit>
                            <List component="div" disablePadding style={{ marginTop: "0.5rem" }}>
                              {/* Conditionally render Info List Item if info exists */}
                              {module.resources?.map((resource, i) => {
                                const { type, name, icon } = resource;
                                const IconComponent = MuiIcons[icon] || MuiIcons["Info"]; // fallback to Info icon

                                return (
                                  <ListItem
                                    key={resource.id?.$oid || i}
                                    onClick={() => {
                                      setSelectedContentIndex(i); // allow multiple of same type
                                      analytics.track(events.VIEW_PAGE, {
                                        page: `module-${type.toLowerCase()}`,
                                        userId: user,
                                        courseId: courseId,
                                        moduleIndex: index,
                                      });
                                    }}
                                    style={{
                                      backgroundColor:
                                        selectedContentIndex === i
                                          ? "#f0f0f0"
                                          : "white",
                                      paddingLeft: "1rem",
                                    }}
                                  >
                                    <ListItemIcon>
                                      <IconComponent />
                                    </ListItemIcon>
                                    <ListItemText secondary={name} />
                                  </ListItem>
                                );
                              })}
                              <ListItem
                                style={{
                                  paddingTop: "10px",
                                  borderBottom: "1px solid #e0e0e0",
                                }}>

                              </ListItem>
                            </List>
                          </Collapse>
                        </div>
                      )
                    })}
                </List>
              </Grid>
            )}
            <Grid item xs={12} md={showModulesList ? 9 : 12}>
              <MDTypography variant="h4" mt={2}>
                {selectedModuleData.name}
              </MDTypography>
              {(isPreviewMode || isPaidPreview) && selectedModule > 0 ? (
                <>
                  {modules[selectedModule]?.resources?.[0]?.type === "MARKDOWN" ? (
                    <>
                      <MDTypography variant="body2">
                        <div data-color-mode="light">
                          <MDEditor.Markdown
                            source={modules[selectedModule].resources[0].description}
                            style={{ color: "#344767", background: "transparent" }}
                            components={{ a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener" referrerPolicy="origin" /> }}
                          />
                        </div>
                      </MDTypography>
                      <MDTypography variant="body2">
                        <div data-color-mode="light">
                          <MDEditor.Markdown
                            source={modules[selectedModule].resources[0].markdown}
                            style={{ color: "#344767", background: "transparent" }}
                            components={{ a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener" referrerPolicy="origin" /> }}
                          />
                        </div>
                      </MDTypography>
                    </>
                  ) : null}
                  <MDBox
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    py={6}
                    sx={{ border: "1px dashed #e0e0e0", borderRadius: "8px", mt: 3 }}
                  >
                    <LockIcon sx={{ fontSize: 60, color: "grey.400" }} />
                    <MDTypography variant="h5" mt={2}>This module is locked</MDTypography>
                    <MDTypography variant="body2" mt={1} color="secondary">
                      {isPaidPreview
                        ? "Purchase the course to access all modules and resources."
                        : "Register to access all course modules and resources."}
                    </MDTypography>
                    <MDButton color="info" sx={{ mt: 3 }} onClick={handleLockedClick}>
                      {isPaidPreview ? "Purchase Course" : "Register Now"}
                    </MDButton>
                  </MDBox>
                  <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={3} mb={2}>
                    <MDButton
                      color="info"
                      disabled={selectedModule === 0 && selectedContentIndex === 0}
                      onClick={handlePrevious}
                    >
                      {selectedContentIndex === 0 ? "Previous Module" : "Previous"}
                    </MDButton>
                    <MDButton
                      color="info"
                      disabled={isLastModule && isModuleEnd}
                      onClick={handleNext}
                    >
                      {isModuleEnd ? "Next Module" : "Next"}
                    </MDButton>
                  </MDBox>
                </>
              ) : (<>
              {/* <MDBox
                style={{
                  border: "1px solid #e0e0e0",
                  borderLeft: "1px solid #e0e0e0",
                  paddingLeft: "2rem",
                  paddingRight: "2rem",
                  paddingTop: "1rem",
                  paddingBottom: "1rem",
                  borderRadius: "5px",
                }}
              > */}
              <MDTypography variant="body2">
                <div data-color-mode="light">
                  <MDEditor.Markdown
                    source={selectedModuleData.resources[selectedContentIndex].description}
                    style={{
                      color: "#344767",
                      background: "transparent",
                    }}
                    components={{
                      a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener" referrerPolicy="origin" />
                    }}
                  />
                </div>
              </MDTypography>
              {selectedType === "MARKDOWN" && (
                <Grid item xs={12} pt={0}>
                  {(
                    <MDBox>
                      <MDTypography variant="body2">
                        <div data-color-mode="light">
                          <MDEditor.Markdown
                            source={selectedModuleData.resources[selectedContentIndex].markdown}
                            style={{
                              color: "#344767",
                              background: "transparent",
                            }}
                            components={{
                              a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener" referrerPolicy="origin" />
                            }}
                          />
                        </div>
                      </MDTypography>
                    </MDBox>
                  )}
                </Grid>
              )}


              {selectedType === "VIDEO" && selectedModuleData.resources[selectedContentIndex].url && (
                <Grid item xs={12} pt={2}>
                  <div>
                    {/* Check if the video is an iframe and handle Vimeo or YouTube embedding */}
                    {selectedModuleData.resources[selectedContentIndex].url.includes("<iframe") && selectedModuleData.resources[selectedContentIndex].url.includes("vimeo") ? (
                      <div dangerouslySetInnerHTML={{ __html: selectedModuleData.sub_contents.video }} />
                    ) : (
                      <></>
                    )}

                    {selectedModuleData.resources[selectedContentIndex].url.includes("<iframe") && selectedModuleData.resources[selectedContentIndex].url.includes("youtube") ? (
                      <div dangerouslySetInnerHTML={{ __html: selectedModuleData.sub_contents.video }} />
                    ) : (
                      <></>
                    )}

                    {/* If it's not an iframe, use ReactPlayer for direct URL */}
                    {selectedModuleData.resources[selectedContentIndex].url && !selectedModuleData.resources[selectedContentIndex].url.includes("<iframe") && (
                      <div style={{
                        position: "relative",
                        paddingTop: "56.25%", /* 16:9 aspect ratio */
                      }}>
                        <ReactPlayer
                          url={selectedModuleData.resources[selectedContentIndex].url}
                          controls
                          config={{
                            file: {
                              attributes: {
                                controlsList: 'nodownload',
                              },
                            },
                          }}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                          }}
                          width={"100%"}
                          height={"100%"}
                        />
                      </div>
                    )}
                  </div>
                </Grid>
              )}




              {/* Display the pdf */}
              {selectedType === "SLIDE" && selectedModuleData.resources[selectedContentIndex].url && (
                <Grid item xs={12} pt={2}>
                  <div
                    style={{
                      width: "100%",
                      // no fixed height here
                    }}
                  >
                    <iframe
                      src={selectedModuleData.resources[selectedContentIndex].url + `?content-disposition=inline`}
                      // src={`${selectedModuleData.reading}#toolbar=0`}
                      // sandbox="allow-downloads"
                      title="PDF Document"
                      style={{
                        width: "100%",
                        minHeight: window.innerWidth < 400 ? "200px" : "600px",
                        border: "none",
                      }}
                    />

                  </div>
                </Grid>
              )}



              {/* <Grid item xs={12} pt={2}>
                {selectedType === "quiz" && selectedModuleData.sub_contents?.quiz && (
                  <AssessmentComponent
                    courseName={courseName}
                    quizLoader={quizLoader}
                    quizQuestions={selectedModuleData.sub_contents?.quiz}
                    currentQuestion={currentQuestion}
                    setCurrentQuestion={setCurrentQuestion}
                    quizCertificate={quizCertificate}
                    quizResults={quizResults}
                    quizSubmitted={quizSubmitted}
                    answeredQuestions={selectedModuleData.sub_contents?.quiz}
                    setAnsweredQuestions={setAnsweredQuestions}
                    submitQuizResults={submitQuizResults}
                    performanceByModule={performanceByModule}
                    performance_data={performance_data}
                    detailResponse={detailResponse}
                    user={user}
                    courseId={courseId}
                    showName={false}
                    showPerformanceData={false}
                  />
                )}
              </Grid> */}
              <Grid item xs={12} pt={2}>

                <MDTypography variant="body2">
                  <div data-color-mode="light">
                    <MDEditor.Markdown
                      source={selectedModuleData.resources[selectedContentIndex].followup}
                      style={{
                        color: "#344767",
                        background: "transparent",
                      }}
                      components={{
                        a: ({ node, ...props }) => <a {...props} target="_blank" rel="noopener" referrerPolicy="origin" />
                      }}
                    />
                  </div>
                </MDTypography>
              </Grid>
              {/* Create two buttons for the previous and next navigation that will be to the left and right ends*/}
              <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mt={3}
                mb={2}
              >
                <MDButton
                  color="info"
                  disabled={(selectedModule === 0 && selectedContentIndex === 0) || (!isPreviewMode && !isPaidPreview && !coursePurchaseStatus)}
                  onClick={handlePrevious}
                >
                  {selectedContentIndex === 0 ? 'Previous Module' : 'Previous'}
                </MDButton>
                <MDBox display="flex" gap={1} >
                  {isModuleEnd &&
                    !completedModules.includes(selectedModuleData._id) &&
                    !isPreviewMode &&
                    coursePurchaseStatus ? (
                    <MDButton color="success" onClick={handleMarkAsComplete}>
                      Mark as completed
                    </MDButton>
                  ) : null}

                  <MDButton
                    color="info"
                    disabled={
                      isPreviewMode ? (isLastModule && isModuleEnd) :
                      isPaidPreview ? (isLastModule && isModuleEnd) : (
                        !coursePurchaseStatus ||               // 3) must be purchased
                        (isModuleEnd && !isModuleCompleted) || // 1) only block at module boundary
                        (isLastModule && isModuleEnd)          // 2/4) nothing beyond final module
                      )
                    }
                    onClick={isPaidPreview && isModuleEnd && selectedModule === 0 ? handleLockedClick : handleNext}
                  >
                    {isModuleEnd ? 'Next Module' : 'Next'}
                  </MDButton>
                </MDBox>

              </MDBox>
            </>)}
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
      <CourseGoalModal
        open={goalModalOpen}
        onClose={() => setGoalModalOpen(false)}
        onSelectGoal={handlePurchaseCourse}
        modalTitle="What do you want to accomplish with this course?"
        modalDescription="Choose a learning path that matches your goals."
      />
      <Dialog
        open={registrationPromptOpen}
        onClose={() => setRegistrationPromptOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px", overflow: "hidden" } }}
      >
        <DialogTitle sx={{ background: "#2563eb", py: 2.25 }}>
          <MDBox display="flex" alignItems="center" gap={1}>
            <Icon sx={{ color: "white" }}>verified_user</Icon>
            <MDTypography variant="h6" color="white">
              Registration Required
            </MDTypography>
          </MDBox>
        </DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          <MDBox
            sx={{
              backgroundColor: "#f8fbff",
              border: "1px solid #dbeafe",
              borderRadius: "10px",
              p: 2,
            }}
          >
            <MDTypography variant="body2">
              You need to register for this course to continue.
            </MDTypography>
            <MDBox display="flex" alignItems="center" mt={1}>
              <Icon fontSize="small" color="info" sx={{ mr: 0.75 }}>
                open_in_new
              </Icon>
              <MDTypography variant="body2">
                Clicking Register will take you to an external site.
              </MDTypography>
            </MDBox>
          </MDBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, gap: 1 }}>
          <MDButton variant="outlined" color="secondary" onClick={() => setRegistrationPromptOpen(false)}>
            Cancel
          </MDButton>
          <MDButton color="info" onClick={handleRegisterFromPrompt}>
            <Icon sx={{ fontSize: "1rem", mr: 0.5 }}>open_in_new</Icon>
            Register
          </MDButton>
        </DialogActions>
      </Dialog>
    </Card >
  );
}

export default CourseModule;
